import React, { useMemo } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { Grid, Button, TextField } from "@mui/material";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { useListGlobalListQuery } from "store/services/globalListApi";
import { GLOBAL_LIST_CONSTANTS } from "data/static/globalListOptions";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import { useListListQuery } from "store/services/listService";
const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split("T")[0]; 
};

const INITIAL_VALUE = {
  collectionMethod: "",
  bankName: null,
  collectedDate: getTodayDate(), 
};


const DeliveryCollectionFinanceForm = ({ onSubmit = () => {}, isBusy = false, onCancel = () => {} }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: INITIAL_VALUE,
    mode: "onChange",
  });

  const { data: bankNameOptions, isLoading: loadingBankNameOptions } =
    useListGlobalListQuery({
      type: GLOBAL_LIST_CONSTANTS.BANK_NAME,
      isSubType: false,
      includeChildren: true,
      status: "Active",
    });

  const { data: entityTypeList = [] } = useListListQuery(
    {
      type: ENTITY_LIST_CONSTANTS.PAYMENT_METHOD,
      status: "Active",
    },
    { skip: false, refetchOnMountOrArgChange: true }
  );

  // Memoize mapped lists
  const _mappedAccountTypeList = useMemo(
    () =>
      Array.isArray(entityTypeList)
        ? entityTypeList.map((item) => ({
            label: item?.name || "",
            value: item?._id || "",
          }))
        : [],
    [entityTypeList]
  );

  const _watchTransferMode = useWatch({
    control,
    name: "collectionMethod",
  });

  const selectedCollectionMethod = useMemo(() => {
    if (!Array.isArray(entityTypeList) || !_watchTransferMode) return null;
    return entityTypeList.find((item) => item._id === _watchTransferMode);
  }, [_watchTransferMode, entityTypeList]);

  const handleFormSubmit = (data) => {
    const payload = {
      ...data,
      bankName:
        selectedCollectionMethod?.name === "Bank" ? data.bankName : null,
    };
    onSubmit(payload);
  };

  return (
    <AppForm loading={isBusy || loadingBankNameOptions}>
      <AppForm.Content>
        <Grid container spacing={3}>
          <Grid item md={6}>
            <Controller
              name="collectionMethod"
              control={control}
              rules={{ required: "Collection Method is required" }}
              render={({ field }) => (
                <BaseSelect
                  fullWidth
                  required
                  label="Collection Method"
                  options={_mappedAccountTypeList}
                  getOptionLabel={(opt) => opt.label || ""}
                  getOptionValue={(opt) => opt.value || ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  error={!!errors.collectionMethod}
                  helperText={errors.collectionMethod?.message}
                />
              )}
            />
          </Grid>

          {selectedCollectionMethod?.name === "Bank" && (
            <Grid item md={6}>
              <Controller
                name="bankName"
                control={control}
                rules={{ required: "Bank Name is required" }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    required
                    label="Bank Name"
                    value={
                      bankNameOptions?.find((opt) => opt._id === field.value) ??
                      null
                    }
                    onChange={(_, data) => field.onChange(data?._id || null)}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={bankNameOptions || []}
                    loading={loadingBankNameOptions}
                    loadingText="Loading Bank Names..."
                    error={!!errors.bankName}
                    helperText={errors.bankName?.message}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item md={6}>
            <Controller
              name="collectedDate"
              control={control}
              rules={{
                required: "Collected Date is required",
                validate: (value) => {
                  const selectedDate = new Date(value);
                  const today = new Date();
                  selectedDate.setHours(0, 0, 0, 0);
                  today.setHours(0, 0, 0, 0);
                  if (selectedDate > today) {
                    return "Future dates are not allowed";
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Collected Date"
                  required
                  disabled
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.collectedDate}
                  helperText={errors.collectedDate?.message}
                />
              )}
            />
          </Grid>
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button
          onClick={handleSubmit(handleFormSubmit)}
          variant="contained"
          color="primary"
          disabled={isBusy}
        >
          Approve
        </Button>
        <Button
          onClick={onCancel}
          variant="outlined"
          color="secondary"
          disabled={isBusy}
          style={{ marginLeft: "10px" }}
        >
          Cancel
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default DeliveryCollectionFinanceForm;